import { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Check, 
  X, 
  AlertTriangle,
  Filter,
  Calendar as CalendarIcon,
  Users,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { Calendar } from './ui/calendar';
import { Checkbox } from './ui/checkbox';
import { ScrollArea } from './ui/scroll-area';
import { format, parseISO } from 'date-fns';
import { fr, de, enUS } from 'date-fns/locale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ManagerValidationView() {
  const { t, language } = useLanguage();
  const locale = language === 'de' ? de : language === 'en' ? enUS : fr;
  
  // State
  const [collaboratorsAlert, setCollaboratorsAlert] = useState([]);
  const [validations, setValidations] = useState([]);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [stateFilter, setStateFilter] = useState('pending');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [showUserDropdown, setShowUserDropdown] = useState(false);

  // Fetch collaborators with pending validations (alert)
  const fetchCollaboratorsAlert = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/planning-validations/collaborators-alert`);
      setCollaboratorsAlert(res.data);
    } catch (err) {
      console.error('Error fetching collaborators alert:', err);
    }
  }, []);

  // Fetch team members
  const fetchTeam = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/team`);
      setTeam(res.data);
    } catch (err) {
      console.error('Error fetching team:', err);
    }
  }, []);

  // Fetch validations with filters
  const fetchValidations = useCallback(async () => {
    try {
      setLoading(true);
      const params = { state: stateFilter };
      
      if (selectedUsers.length > 0) {
        params.user_ids = selectedUsers.join(',');
      }
      if (dateFrom) {
        params.date_from = format(dateFrom, 'yyyy-MM-dd');
      }
      if (dateTo) {
        params.date_to = format(dateTo, 'yyyy-MM-dd');
      }
      
      const res = await axios.get(`${API}/planning-validations/manager-view`, { params });
      setValidations(res.data);
    } catch (err) {
      console.error('Error fetching validations:', err);
    } finally {
      setLoading(false);
    }
  }, [stateFilter, selectedUsers, dateFrom, dateTo]);

  useEffect(() => {
    fetchCollaboratorsAlert();
    fetchTeam();
  }, [fetchCollaboratorsAlert, fetchTeam]);

  useEffect(() => {
    fetchValidations();
  }, [fetchValidations]);

  // Handle approve/reject
  const handleReview = async (validationId, status) => {
    try {
      await axios.put(`${API}/planning-validations/${validationId}/review`, { status });
      toast.success(status === 'approved' ? t('modificationApprovedSuccess') : t('modificationRejectedSuccess'));
      fetchValidations();
      fetchCollaboratorsAlert();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error'));
    }
  };

  // Toggle user selection
  const toggleUser = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  // Format date
  const formatDate = (dateStr) => {
    try {
      const date = parseISO(dateStr);
      return format(date, 'EEE dd/MM', { locale });
    } catch {
      return dateStr;
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    switch(status) {
      case 'pending':
        return <Badge className="bg-amber-500 text-white rounded">{t('modificationPending')}</Badge>;
      case 'approved':
        return <Badge className="bg-emerald-500 text-white rounded">{t('modificationApproved')}</Badge>;
      case 'rejected':
        return <Badge className="bg-red-500 text-white rounded">{t('modificationRejected')}</Badge>;
      case 'validated':
        return <Badge className="bg-blue-500 text-white rounded">{t('validated')}</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6" data-testid="manager-validation-view">
      {/* Collaborators Alert */}
      {collaboratorsAlert.length > 0 && (
        <div className="border border-amber-300 bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4" data-testid="collaborators-alert">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            <h3 className="font-semibold text-amber-800 dark:text-amber-400">{t('collaboratorsWithPendingDays')}</h3>
          </div>
          <div className="flex flex-wrap gap-3">
            {collaboratorsAlert.map((collab) => (
              <div
                key={collab.user_id}
                className="flex items-center gap-2 bg-white dark:bg-gray-800 border border-amber-200 dark:border-amber-700 rounded-lg px-3 py-2"
                data-testid={`alert-${collab.user_id}`}
              >
                <span
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: collab.color }}
                />
                <span className="font-medium text-sm">{collab.user_name}</span>
                <Badge className="bg-amber-500 text-white rounded-full text-xs">
                  {collab.pending_days} {t('daysToValidate')}
                </Badge>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-4 p-4 border border-border rounded-lg bg-card" data-testid="validation-filters">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">{t('filters')}:</span>
        </div>

        {/* State Filter */}
        <Select value={stateFilter} onValueChange={setStateFilter}>
          <SelectTrigger className="w-[160px]" data-testid="state-filter">
            <SelectValue placeholder={t('stateFilter')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="pending">{t('toValidate')}</SelectItem>
            <SelectItem value="modified">{t('modifiedState')}</SelectItem>
            <SelectItem value="not_modified">{t('notModifiedState')}</SelectItem>
            <SelectItem value="all">{t('allStates')}</SelectItem>
          </SelectContent>
        </Select>

        {/* Collaborators Filter */}
        <Popover open={showUserDropdown} onOpenChange={setShowUserDropdown}>
          <PopoverTrigger asChild>
            <Button variant="outline" className="w-[200px] justify-start" data-testid="user-filter">
              <Users className="h-4 w-4 mr-2" />
              {selectedUsers.length === 0 
                ? t('allCollaborators')
                : `${selectedUsers.length} ${t('selected')}`
              }
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[250px] p-0">
            <div className="p-2 border-b border-border">
              <Button 
                variant="ghost" 
                size="sm" 
                className="w-full justify-start"
                onClick={() => setSelectedUsers([])}
              >
                {t('allCollaborators')}
              </Button>
            </div>
            <ScrollArea className="h-[200px]">
              <div className="p-2 space-y-1">
                {team.map((member) => (
                  <div
                    key={member.id}
                    className="flex items-center gap-2 p-2 rounded hover:bg-muted cursor-pointer"
                    onClick={() => toggleUser(member.id)}
                  >
                    <Checkbox checked={selectedUsers.includes(member.id)} />
                    <span
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: member.color || '#3B82F6' }}
                    />
                    <span className="text-sm">{member.first_name} {member.last_name}</span>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </PopoverContent>
        </Popover>

        {/* Date Range Filter */}
        <div className="flex items-center gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" data-testid="date-from-filter">
                <CalendarIcon className="h-4 w-4 mr-2" />
                {dateFrom ? format(dateFrom, 'dd/MM/yyyy') : t('fromDate')}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={dateFrom}
                onSelect={setDateFrom}
                locale={locale}
                weekStartsOn={1}
              />
            </PopoverContent>
          </Popover>
          <span className="text-muted-foreground">→</span>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" data-testid="date-to-filter">
                <CalendarIcon className="h-4 w-4 mr-2" />
                {dateTo ? format(dateTo, 'dd/MM/yyyy') : t('toDate')}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={dateTo}
                onSelect={setDateTo}
                locale={locale}
                weekStartsOn={1}
              />
            </PopoverContent>
          </Popover>
          {(dateFrom || dateTo) && (
            <Button variant="ghost" size="sm" onClick={() => { setDateFrom(null); setDateTo(null); }}>
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Validations List */}
      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : validations.length === 0 ? (
        <div className="border border-border rounded-lg p-8 text-center">
          <CheckCircle className="h-12 w-12 mx-auto text-emerald-500 mb-4" />
          <p className="text-muted-foreground">{t('noValidationsToReview')}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {validations.map((validation) => (
            <div
              key={validation.id}
              className={`border rounded-lg overflow-hidden ${
                validation.status === 'pending' 
                  ? 'border-amber-300 bg-amber-50/50 dark:bg-amber-900/10' 
                  : 'border-border bg-card'
              }`}
              data-testid={`validation-${validation.id}`}
            >
              {/* Row 1: Date + Original Planning + Actions */}
              <div className="grid grid-cols-[100px_1fr_80px_80px_60px_80px_auto] gap-2 items-center p-3 border-b border-border/50 bg-muted/30">
                <div className="font-semibold text-sm">{formatDate(validation.date)}</div>
                <div className="text-xs text-muted-foreground">{t('originalPlanning')}</div>
                <div className="text-sm font-mono">{validation.original_start_time}</div>
                <div className="text-sm font-mono">{validation.original_end_time}</div>
                <div className="text-sm text-muted-foreground">{validation.original_break_minutes || 0}min</div>
                <div className="text-sm font-semibold">{validation.original_effective_hours}h</div>
                <div className="flex items-center gap-1 justify-end">
                  {validation.status === 'pending' && (
                    <>
                      <Button
                        size="sm"
                        className="h-7 px-2 bg-emerald-600 hover:bg-emerald-700"
                        onClick={() => handleReview(validation.id, 'approved')}
                        data-testid={`approve-btn-${validation.id}`}
                      >
                        <Check className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        className="h-7 px-2"
                        onClick={() => handleReview(validation.id, 'rejected')}
                        data-testid={`reject-btn-${validation.id}`}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                  {validation.status !== 'pending' && getStatusBadge(validation.status)}
                </div>
              </div>

              {/* Row 2: Name + Proposed Modification (only for modify type) */}
              {validation.validation_type === 'modify' && (
                <div className="grid grid-cols-[100px_1fr_80px_80px_60px_80px_auto] gap-2 items-center p-3 border-b border-border/50">
                  <div className="flex items-center gap-2">
                    <span
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: validation.user_color }}
                    />
                    <span className="font-medium text-sm">{validation.user_first_name}</span>
                  </div>
                  <div className="text-xs text-muted-foreground">{t('proposedModification')}</div>
                  <div className="text-sm font-mono text-primary">{validation.proposed_start_time}</div>
                  <div className="text-sm font-mono text-primary">{validation.proposed_end_time}</div>
                  <div className="text-sm text-muted-foreground">{validation.proposed_break_minutes || 0}min</div>
                  <div className="text-sm font-semibold text-primary">{validation.proposed_effective_hours}h</div>
                  <div></div>
                </div>
              )}

              {/* Row 3: Last name + Reason */}
              <div className="grid grid-cols-[100px_1fr] gap-2 items-start p-3">
                <div className="text-sm text-muted-foreground">{validation.user_last_name}</div>
                <div className="text-sm">
                  {validation.reason ? (
                    <span className="italic text-muted-foreground">"{validation.reason}"</span>
                  ) : validation.validation_type === 'confirm' ? (
                    <span className="text-muted-foreground">— {t('conformSchedule')}</span>
                  ) : null}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
